import { motion } from "framer-motion";
import { Zap, UserCheck, Lock } from "lucide-react";
import { whatsappUrl, trackWaClick } from "../lib/whatsapp";

const ease = [0.22, 1, 0.36, 1];

// Module-level motion configs — stable references prevent unnecessary re-renders
const headInitial = { opacity: 0, y: 24 };
const headAnimate = { opacity: 1, y: 0 };
const headTransition = { duration: 0.7, ease };
const headViewport = { once: true, margin: "-80px" };

const cardInitial = { opacity: 0, y: 20 };
const cardAnimate = { opacity: 1, y: 0 };
const cardViewport = { once: true, margin: "-60px" };

const reasons = [
  {
    id: "instant",
    icon: Zap,
    title: "Sofort startklar",
    desc: "Keine Registrierung, kein Formular. Ein Tap – WhatsApp öffnet sich mit der fertigen Nachricht.",
  },
  {
    id: "personal",
    icon: UserCheck,
    title: "Persönliche Beratung",
    desc: "Du chattest direkt mit einem echten Berater aus Herford. Keine Bots, keine Callcenter.",
  },
  {
    id: "privacy",
    icon: Lock,
    title: "Datenschutz-konform",
    desc: "Nur du entscheidest, was du teilst. Ende-zu-Ende verschlüsselt – bis zu deinem Angebot.",
  },
];

export default function WhyWhatsAppSection() {
  return (
    <section
      id="warum-whatsapp"
      className="relative py-20 sm:py-28 bg-brand-paper overflow-hidden"
      data-testid="why-whatsapp-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={headInitial}
            whileInView={headAnimate}
            viewport={headViewport}
            transition={headTransition}
            className="lg:col-span-5"
          >
            <p className="text-[11px] font-bold tracking-[0.25em] text-brand-energy uppercase">Warum WhatsApp?</p>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl font-black text-brand-ink tracking-[-0.035em] leading-[0.95]">
              Weil einfach<br />einfach besser ist.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-brand-muted max-w-md leading-relaxed">
              Wir haben den typischen „Formular-auf-Website"-Ablauf weggelassen. Das spart dir Zeit –
              und uns auch. So kriegst du dein Angebot schneller.
            </p>

            <a
              href={whatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWaClick("why_whatsapp")}
              className="mt-8 inline-flex items-center gap-2 link-underline font-semibold text-brand-ink"
              data-testid="why-whatsapp-link"
            >
              Jetzt Chat starten <span className="arrow-shift">→</span>
            </a>
          </motion.div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {reasons.map((r, i) => (
              <motion.div
                key={r.id}
                initial={cardInitial}
                whileInView={cardAnimate}
                viewport={cardViewport}
                transition={{ duration: 0.6, ease, delay: 0.1 + i * 0.08 }}
                className="group bg-brand-surface border border-brand-line rounded-3xl p-6 hover:border-brand-ink transition-colors"
                data-testid={`why-item-${r.id}`}
              >
                <div className="w-11 h-11 rounded-2xl bg-brand-energy flex items-center justify-center mb-5 transition-transform duration-500 group-hover:scale-110">
                  <r.icon className="w-5 h-5" style={{ color: '#0A0A0A' }} strokeWidth={2.4} />
                </div>
                <h3 className="font-heading text-lg font-black tracking-tight text-brand-ink">{r.title}</h3>
                <p className="mt-2 text-sm text-brand-muted leading-relaxed">{r.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
